// src/components/layout/Layout.js
import React, { useState, useEffect } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { styled, useTheme } from '@mui/material/styles';
import { 
  Box, 
  Drawer, 
  AppBar, 
  Toolbar, 
  List, 
  Typography, 
  Divider, 
  IconButton, 
  ListItem, 
  ListItemButton, 
  ListItemIcon, 
  ListItemText,
  Avatar,
  Menu,
  MenuItem,
  useMediaQuery,
  Chip,
  Badge
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import DashboardIcon from '@mui/icons-material/Dashboard';
import PeopleIcon from '@mui/icons-material/People';
import ChildCareIcon from '@mui/icons-material/ChildCare';
import NotificationsIcon from '@mui/icons-material/Notifications';
import SchoolIcon from '@mui/icons-material/School';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import ReceiptIcon from '@mui/icons-material/Receipt';
import AccountBalanceIcon from '@mui/icons-material/AccountBalance';
import SettingsIcon from '@mui/icons-material/Settings';
import ExitToAppIcon from '@mui/icons-material/ExitToApp';
import EventIcon from '@mui/icons-material/Event';
import PollIcon from '@mui/icons-material/Poll';
import AccountCircleIcon from '@mui/icons-material/AccountCircle';
import PhoneAndroidIcon from '@mui/icons-material/PhoneAndroid';

import { auth } from '../../firebase';
import { signOut } from 'firebase/auth';

// Paleta de colores verde pastel
const COLORS = {
  primary: '#A8E6CF',      // Verde pastel claro
  secondary: '#88D8A3',    // Verde pastel medio
  accent: '#68C683',       // Verde pastel oscuro
  success: '#7ED321',      // Verde éxito
  background: '#F8FDF8',   // Fondo muy claro
  surface: '#FFFFFF',      // Blanco
  text: {
    primary: '#2C3E50',    // Gris oscuro
    secondary: '#7F8C8D',  // Gris medio
    light: '#FFFFFF'       // Blanco
  },
  sidebar: {
    background: '#FFFFFF',
    header: '#E8F5E8',
    hover: '#F0F8F0',
    selected: '#A8E6CF',
    selectedText: '#2C3E50'
  },
  appbar: {
    background: 'linear-gradient(135deg, #88D8A3 0%, #68C683 100%)',
    text: '#FFFFFF'
  }
};

// Alias para compatibilidad con footer
const Colors = COLORS;

const drawerWidth = 260;

const Main = styled('main', { shouldForwardProp: (prop) => prop !== 'open' })(
  ({ theme, open }) => ({
    flexGrow: 1,
    minWidth: 0,
    padding: theme.spacing(3),
    backgroundColor: '#f4f6f8',
    minHeight: '100vh',
    transition: theme.transitions.create('margin', {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
    marginLeft: `-${drawerWidth}px`,
    [theme.breakpoints.down('md')]: {
      marginLeft: 0,
      padding: theme.spacing(2),
    },
    ...(open && {
      transition: theme.transitions.create('margin', {
        easing: theme.transitions.easing.easeOut,
        duration: theme.transitions.duration.enteringScreen,
      }),
      marginLeft: 0,
      [theme.breakpoints.down('md')]: {
        marginLeft: 0,
      },
    }),
  }),
);

const AppBarStyled = styled(AppBar, { shouldForwardProp: (prop) => prop !== 'open' })(
  ({ theme, open }) => ({
    background: COLORS.appbar.background,
    boxShadow: '0 4px 20px rgba(0,0,0,0.1)',
    transition: theme.transitions.create(['margin', 'width'], {
      easing: theme.transitions.easing.sharp,
      duration: theme.transitions.duration.leavingScreen,
    }),
    [theme.breakpoints.up('md')]: {
      ...(open && {
        width: `calc(100% - ${drawerWidth}px)`,
        marginLeft: `${drawerWidth}px`,
        transition: theme.transitions.create(['margin', 'width'], {
          easing: theme.transitions.easing.easeOut,
          duration: theme.transitions.duration.enteringScreen,
        }),
      }),
    },
  }),
);

const DrawerHeader = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  padding: theme.spacing(2),
  background: COLORS.sidebar.header,
  borderBottom: `2px solid ${COLORS.primary}`,
  minHeight: 80,
  justifyContent: 'space-between',
}));

const StyledDrawer = styled(Drawer)(({ theme }) => ({
  width: drawerWidth,
  flexShrink: 0,
  '& .MuiDrawer-paper': {
    width: drawerWidth,
    boxSizing: 'border-box',
    backgroundColor: COLORS.sidebar.background,
    borderRight: `3px solid ${COLORS.primary}`,
    boxShadow: '4px 0 15px rgba(0,0,0,0.1)',
  },
}));

const StyledListItemButton = styled(ListItemButton)(({ theme, selected }) => ({
  margin: theme.spacing(0.5, 1),
  borderRadius: theme.spacing(1.5),
  transition: 'all 0.3s ease',
  minHeight: 48,
  '&:hover': {
    backgroundColor: COLORS.sidebar.hover,
    transform: 'translateX(4px)',
    boxShadow: '0 4px 12px rgba(0,0,0,0.1)',
  },
  ...(selected && {
    backgroundColor: COLORS.sidebar.selected,
    color: COLORS.sidebar.selectedText,
    fontWeight: 'bold',
    boxShadow: '0 4px 12px rgba(168, 230, 207, 0.4)',
    '&:hover': {
      backgroundColor: COLORS.sidebar.selected,
      transform: 'translateX(4px)',
    },
    '& .MuiListItemIcon-root': {
      color: COLORS.sidebar.selectedText,
    },
    '& .MuiListItemText-primary': {
      fontWeight: 'bold',
    },
  }),
}));

const Layout = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  const [open, setOpen] = useState(!isMobile);
  const [anchorEl, setAnchorEl] = useState(null);
  const [user, setUser] = useState(null);

  // Manejar cambios en el estado de autenticación
  useEffect(() => {
    let isMounted = true;
    
    const unsubscribe = auth.onAuthStateChanged((currentUser) => {
      if (isMounted) {
        setUser(currentUser);
      }
    });

    return () => {
      isMounted = false;
      unsubscribe();
    };
  }, []);

  // Cerrar sidebar en móvil cuando cambie la ruta
  useEffect(() => {
    if (isMobile) {
      setOpen(false);
    }
  }, [location.pathname, isMobile]);

  // Ajustar sidebar según el tamaño de pantalla
  useEffect(() => {
    setOpen(!isMobile);
  }, [isMobile]);

  const handleDrawerToggle = () => {
    setOpen(!open);
  };

  const handleMenu = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = async () => {
    try {
      await signOut(auth);
      navigate('/login', { replace: true });
    } catch (error) {
      console.error('Error al cerrar sesión:', error);
    } finally {
      handleClose();
    }
  };

  const menuItems = [
    {
      text: 'Dashboard',
      icon: <DashboardIcon />,
      path: '/admin',
      badge: null,
      category: 'main'
    },
    {
      text: 'Usuarios',
      icon: <PeopleIcon />,
      path: '/admin/users',
      badge: null,
      category: 'users'
    },
    {
      text: 'Hijos',
      icon: <ChildCareIcon />,
      path: '/admin/children',
      badge: null,
      category: 'users'
    },
    {
      text: 'Notificaciones',
      icon: <NotificationsIcon />,
      path: '/admin/notifications',
      badge: null,
      category: 'content'
    },
    {
      text: 'Eventos',
      icon: <EventIcon />,
      path: '/admin/events',
      badge: null,
      category: 'content'
    },
    {
      text: 'Encuestas',
      icon: <PollIcon />,
      path: '/admin/surveys',
      badge: null,
      category: 'content'
    },
    {
      text: 'Servicios',
      icon: <SchoolIcon />,
      path: '/admin/services',
      badge: null,
      category: 'commerce'
    },
    {
      text: 'Productos',
      icon: <ShoppingCartIcon />,
      path: '/admin/products',
      badge: null,
      category: 'commerce'
    },
    {
      text: 'Pedidos',
      icon: <ReceiptIcon />,
      path: '/admin/orders',
      badge: null,
      category: 'commerce'
    },
    {
      text: 'Contabilidad',
      icon: <AccountBalanceIcon />,
      path: '/admin/accounting',
      badge: null,
      category: 'commerce'
    },
    {
      text: 'Versiones App',
      icon: <PhoneAndroidIcon />,
      path: '/admin/app-versions',
      badge: null,
      category: 'system'
    },
    {
      text: 'Configuración',
      icon: <SettingsIcon />,
      path: '/admin/settings',
      badge: null,
      category: 'system'
    },
  ];

  const menuCategories = {
    main: { label: 'Principal', items: [] },
    users: { label: 'Usuarios', items: [] },
    content: { label: 'Contenido', items: [] },
    commerce: { label: 'Comercio', items: [] },
    system: { label: 'Sistema', items: [] },
  };

  // Agrupar items por categoría
  menuItems.forEach(item => {
    if (menuCategories[item.category]) {
      menuCategories[item.category].items.push(item);
    }
  });

  // Obtener título de la página actual
  const getCurrentPageTitle = () => {
    const currentItem = menuItems.find(item =>
      item.path === location.pathname ||
      (item.path !== '/admin' && location.pathname.startsWith(item.path))
    );

    if (currentItem) return currentItem.text;

    // Rutas específicas
    // Rutas especificas primero (mas concretas antes que genericas)
    const routeMap = [
      ['/admin/notifications/create', 'Nueva Notificación'],
      ['/admin/events/new', 'Nuevo Evento'],
      ['/admin/services/create', 'Nuevo Servicio'],
      ['/admin/products/create', 'Nuevo Producto'],
      ['/admin/surveys/create', 'Nueva Encuesta'],
      ['/admin/surveys/edit/', 'Editar Encuesta'],
      ['/admin/surveys/results/', 'Resultados de Encuesta'],
    ];

    // Comprobar rutas exactas/prefijo primero
    for (const [route, title] of routeMap) {
      if (location.pathname.startsWith(route)) {
        return title;
      }
    }

    // Detectar rutas con /edit al final
    if (location.pathname.match(/\/admin\/events\/[^/]+\/edit$/)) {
      return 'Editar Evento';
    }

    // Rutas genericas de detalle (con ID)
    const detailRoutes = {
      '/admin/users/': 'Detalle de Usuario',
      '/admin/children/': 'Detalle de Hijo',
      '/admin/notifications/': 'Editar Notificación',
      '/admin/events/': 'Detalle de Evento',
      '/admin/services/': 'Detalle de Servicio',
      '/admin/products/': 'Detalle de Producto',
      '/admin/orders/': 'Detalle de Pedido',
      '/admin/surveys/': 'Detalle de Encuesta',
    };

    for (const [route, title] of Object.entries(detailRoutes)) {
      if (location.pathname.startsWith(route)) {
        return title;
      }
    }

    return 'Panel de Administración';
  };

  const getUserDisplayName = () => {
    if (!user) return 'Usuario';
    return user.displayName || user.email?.split('@')[0] || 'Usuario';
  };

  const getUserInitials = () => {
    if (!user) return 'U';
    const name = getUserDisplayName();
    return name.substring(0, 2).toUpperCase();
  };

  const isSelected = (path) => {
    if (path === '/admin') {
      return location.pathname === '/admin';
    }
    return location.pathname.startsWith(path);
  };

  const renderMenuSection = (categoryKey, category) => {
    if (category.items.length === 0) return null;

    return (
      <Box key={categoryKey} sx={{ mb: 1 }}>
        {categoryKey !== 'main' && (
          <Typography 
            variant="overline" 
            sx={{ 
              px: 2, 
              py: 1, 
              display: 'block',
              color: COLORS.text.secondary,
              fontWeight: 'bold',
              fontSize: '0.7rem',
              letterSpacing: '0.05em'
            }}
          >
            {category.label}
          </Typography>
        )}
        <List sx={{ py: 0 }}>
          {category.items.map((item) => (
            <ListItem key={item.text} disablePadding>
              <StyledListItemButton
                selected={isSelected(item.path)}
                onClick={() => {
                  navigate(item.path);
                  if (isMobile) setOpen(false);
                }}
              >
                <ListItemIcon sx={{ minWidth: 40 }}>
                  {item.badge ? (
                    <Badge badgeContent={item.badge} color="error">
                      {item.icon}
                    </Badge>
                  ) : (
                    item.icon
                  )}
                </ListItemIcon>
                <ListItemText 
                  primary={item.text} 
                  primaryTypographyProps={{
                    fontSize: '0.9rem',
                    fontWeight: isSelected(item.path) ? 'bold' : 'normal'
                  }}
                />
              </StyledListItemButton>
            </ListItem>
          ))}
        </List>
        {categoryKey !== 'system' && <Divider sx={{ my: 1, mx: 2 }} />}
      </Box>
    );
  };

  return (
    <Box sx={{ display: 'flex' }}>
      <AppBarStyled position="fixed" open={open && !isMobile}>
        <Toolbar sx={{ minHeight: 64 }}>
          <IconButton
            color="inherit"
            aria-label="toggle drawer"
            onClick={handleDrawerToggle}
            edge="start"
            sx={{ 
              mr: 2,
              color: COLORS.appbar.text,
              '&:hover': {
                backgroundColor: 'rgba(255,255,255,0.1)'
              }
            }}
          >
            <MenuIcon />
          </IconButton>
          
          <Typography 
            variant="h6" 
            noWrap 
            component="div" 
            sx={{ 
              flexGrow: 1,
              color: COLORS.appbar.text,
              fontWeight: 'bold'
            }}
          >
            {getCurrentPageTitle()}
          </Typography>

          <Chip
            label={getUserDisplayName()}
            avatar={
              <Avatar sx={{ 
                bgcolor: COLORS.surface, 
                color: COLORS.text.primary,
                width: 28,
                height: 28,
                fontSize: '0.8rem'
              }}>
                {getUserInitials()}
              </Avatar>
            }
            onClick={handleMenu}
            sx={{
              bgcolor: 'rgba(255,255,255,0.2)',
              color: COLORS.appbar.text,
              '&:hover': {
                bgcolor: 'rgba(255,255,255,0.3)',
              },
              '& .MuiChip-label': {
                px: 1
              }
            }}
          />

          <Menu
            id="menu-appbar"
            anchorEl={anchorEl}
            anchorOrigin={{
              vertical: 'bottom',
              horizontal: 'right',
            }}
            keepMounted
            transformOrigin={{
              vertical: 'top',
              horizontal: 'right',
            }}
            open={Boolean(anchorEl)}
            onClose={handleClose}
            PaperProps={{
              sx: {
                mt: 1,
                borderRadius: 2,
                boxShadow: '0 8px 25px rgba(0,0,0,0.15)',
                border: `1px solid ${COLORS.primary}`,
                minWidth: 180
              }
            }}
          >
            <MenuItem
              onClick={() => {
                navigate('/admin/settings');
                handleClose();
              }}
              sx={{
                '&:hover': {
                  backgroundColor: COLORS.sidebar.hover
                }
              }}
            >
              <ListItemIcon>
                <AccountCircleIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>Mi Perfil</ListItemText>
            </MenuItem>
            <Divider />
            <MenuItem 
              onClick={handleLogout}
              sx={{
                color: COLORS.text.primary,
                '&:hover': {
                  backgroundColor: '#FFEBEE'
                }
              }}
            >
              <ListItemIcon>
                <ExitToAppIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>Cerrar sesión</ListItemText>
            </MenuItem>
          </Menu>
        </Toolbar>
      </AppBarStyled>

      <StyledDrawer
        variant={isMobile ? "temporary" : "persistent"}
        anchor="left"
        open={open}
        onClose={handleDrawerToggle}
        ModalProps={{
          keepMounted: true, // Better open performance on mobile.
        }}
      >
        <DrawerHeader>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Avatar 
              sx={{ 
                bgcolor: COLORS.accent, 
                color: COLORS.text.light,
                width: 40,
                height: 40,
                fontWeight: 'bold'
              }}
            >
              AB
            </Avatar>
            <Box>
              <Typography 
                variant="h6" 
                sx={{ 
                  color: COLORS.text.primary,
                  fontWeight: 'bold',
                  lineHeight: 1.2
                }}
              >
                AMPA del Bosc
              </Typography>
              <Typography 
                variant="caption" 
                sx={{ 
                  color: COLORS.text.secondary,
                  lineHeight: 1
                }}
              >
                Panel de Administración
              </Typography>
            </Box>
          </Box>
          {!isMobile && (
            <IconButton 
              onClick={handleDrawerToggle}
              sx={{
                '&:hover': {
                  backgroundColor: COLORS.sidebar.hover
                }
              }}
            >
              <ChevronLeftIcon />
            </IconButton>
          )}
        </DrawerHeader>

        <Box sx={{ overflow: 'auto', py: 1 }}>
          {Object.entries(menuCategories).map(([key, category]) => 
            renderMenuSection(key, category)
          )}
        </Box>
      </StyledDrawer>

      <Main open={open && !isMobile}>
        <DrawerHeader />
        <Outlet />

        {/* Footer with Legal Links */}
        <Box sx={{
          mt: 'auto',
          py: 3,
          px: 2,
          borderTop: `1px solid ${Colors.primary}`,
          backgroundColor: Colors.background,
          textAlign: 'center'
        }}>
          <Typography variant="body2" color={Colors.textSecondary} sx={{ mb: 2 }}>
            © {new Date().getFullYear()} AMPA del Bosc. Todos los derechos reservados.
          </Typography>
          <Box sx={{
            display: 'flex',
            justifyContent: 'center',
            gap: 3,
            flexWrap: 'wrap',
            '& a': {
              color: Colors.textSecondary,
              textDecoration: 'none',
              fontSize: '0.875rem',
              '&:hover': {
                color: Colors.primary,
                textDecoration: 'underline'
              }
            }
          }}>
            <a href="/privacy-policy" target="_blank" rel="noopener noreferrer">
              Política de Privacidad
            </a>
            <a href="/terms-of-service" target="_blank" rel="noopener noreferrer">
              Términos y Condiciones
            </a>
            <a href="/legal-notice" target="_blank" rel="noopener noreferrer">
              Aviso Legal
            </a>
            <a href="/cookies-policy" target="_blank" rel="noopener noreferrer">
              Política de Cookies
            </a>
          </Box>
        </Box>
      </Main>
    </Box>
  );
};

export default Layout;